/**
 * CLEAR Wireframe Presentation
 * A clean, modern slideshow presenting all designs, decisions, and recommendations
 */

import { useState, useEffect, useCallback } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Play,
  Pause,
  AlertCircle,
  BarChart3,
  Users,
  DollarSign,
  Check,
  Layers,
  Zap,
  Target,
  Shield,
  RefreshCw,
  HelpCircle,
  MessageSquare,
  Layout,
  Box,
  ArrowRight,
  Lightbulb,
  Clock,
  CheckCircle2,
  Circle,
  Quote,
  FileSearch,
  UserCircle,
  Wifi,
  WifiOff,
  MapPin,
  Smartphone,
  Database,
  TrendingUp,
  XCircle,
  Brain,
  Clipboard,
  Globe,
  // New icons for stakeholder slides
  TrendingDown,
  Rocket,
  Calendar,
  AlertTriangle,
  ThumbsUp,
  ThumbsDown,
  Building,
  Server,
  Cloud,
  Lock,
  Coins,
  FileText,
  CheckSquare,
  ArrowUpRight,
  Milestone
} from 'lucide-react';
import { cn } from '../../lib/utils';

// ============================================================================
// PRESENTATION WRAPPER
// ============================================================================

export function WireframePresentation() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const slides = [
    { id: 'title', component: TitleSlide },
    { id: 'overview', component: OverviewSlide },
    // Research section
    { id: 'research-methodology', component: ResearchMethodologySlide },
    { id: 'user-personas', component: UserPersonasSlide },
    { id: 'pain-points', component: PainPointsSlide },
    { id: 'research-findings', component: ResearchFindingsSlide },
    { id: 'research-to-design', component: ResearchToDesignSlide },
    // Design section
    { id: 'design-system', component: DesignSystemSlide },
    { id: 'detection', component: DetectionSlide },
    { id: 'analysis', component: AnalysisSlide },
    { id: 'operations', component: OperationsSlide },
    { id: 'cash-assistance', component: CashAssistanceSlide },
    { id: 'components', component: ComponentsSlide },
    { id: 'features', component: FeaturesSlide },
    { id: 'workflows', component: WorkflowsSlide },
    { id: 'testing', component: TestingSlide },
    // Stakeholder section
    { id: 'impact-roi', component: ImpactROISlide },
    { id: 'current-vs-future', component: CurrentVsFutureSlide },
    { id: 'risk-mitigation', component: RiskMitigationSlide },
    { id: 'implementation-roadmap', component: ImplementationRoadmapSlide },
    { id: 'pilot-program', component: PilotProgramSlide },
    { id: 'technical-architecture', component: TechnicalArchitectureSlide },
    { id: 'budget-resources', component: BudgetResourcesSlide },
    { id: 'progress-dashboard', component: ProgressDashboardSlide },
    { id: 'recommendations', component: RecommendationsSlide },
    { id: 'call-to-action', component: CallToActionSlide },
    { id: 'closing', component: ClosingSlide },
  ];

  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % slides.length);
    setProgress(0);
  }, [slides.length]);

  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + slides.length) % slides.length);
    setProgress(0);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setProgress(0);
  };

  // Auto-play functionality
  useEffect(() => {
    if (!isPlaying) return;

    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          nextSlide();
          return 0;
        }
        return prev + 1;
      });
    }, 80); // 8 seconds per slide

    return () => clearInterval(progressInterval);
  }, [isPlaying, nextSlide]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        prevSlide();
      } else if (e.key === 'Escape') {
        setIsPlaying(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide]);

  const CurrentSlideComponent = slides[currentSlide].component;

  return (
    <div className="fixed inset-0 bg-gray-900 flex flex-col">
      {/* Progress Bar */}
      <div className="h-1 bg-gray-800">
        <div
          className="h-full bg-accent-500 transition-all duration-100"
          style={{ width: `${((currentSlide + progress / 100) / slides.length) * 100}%` }}
        />
      </div>

      {/* Slide Content */}
      <div className="flex-1 overflow-hidden">
        <CurrentSlideComponent />
      </div>

      {/* Navigation Footer */}
      <div className="h-16 bg-gray-900 border-t border-gray-800 flex items-center justify-between px-8">
        {/* Slide Counter */}
        <div className="text-gray-400 text-sm font-mono">
          {String(currentSlide + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
        </div>

        {/* Slide Dots */}
        <div className="flex items-center gap-2">
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              onClick={() => goToSlide(index)}
              className={cn(
                'w-2 h-2 transition-all',
                index === currentSlide
                  ? 'bg-accent-500 w-6'
                  : 'bg-gray-600 hover:bg-gray-500'
              )}
            />
          ))}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
          >
            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
          </button>
          <button
            onClick={prevSlide}
            className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={nextSlide}
            className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SLIDE COMPONENTS
// ============================================================================

function SlideLayout({
  children,
  variant = 'default'
}: {
  children: React.ReactNode;
  variant?: 'default' | 'dark' | 'accent' | 'split';
}) {
  const variants = {
    default: 'bg-white',
    dark: 'bg-gray-900 text-white',
    accent: 'bg-accent-500 text-white',
    split: 'bg-white',
  };

  return (
    <div className={cn('h-full flex flex-col', variants[variant])}>
      {children}
    </div>
  );
}

function TitleSlide() {
  return (
    <SlideLayout variant="dark">
      <div className="flex-1 flex flex-col items-center justify-center px-16">
        <div className="text-accent-500 text-sm font-semibold tracking-widest uppercase mb-6">
          Research-Driven Wireframe Prototypes
        </div>
        <h1 className="text-6xl font-bold text-white mb-6 text-center">
          CLEAR Humanitarian System
        </h1>
        <p className="text-xl text-gray-400 text-center max-w-2xl mb-4">
          Crisis Learning, Early-warning, Anticipation, and Response
        </p>
        <p className="text-sm text-gray-500 text-center max-w-xl mb-12">
          Designed from NRC Sudan field practitioner interviews
        </p>
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-2 text-gray-500">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span>Detection</span>
          </div>
          <div className="w-8 h-px bg-gray-700" />
          <div className="flex items-center gap-2 text-gray-500">
            <BarChart3 className="w-5 h-5 text-blue-500" />
            <span>Analysis</span>
          </div>
          <div className="w-8 h-px bg-gray-700" />
          <div className="flex items-center gap-2 text-gray-500">
            <Users className="w-5 h-5 text-green-500" />
            <span>Operations</span>
          </div>
          <div className="w-8 h-px bg-gray-700" />
          <div className="flex items-center gap-2 text-gray-500">
            <DollarSign className="w-5 h-5 text-amber-500" />
            <span>Cash Assistance</span>
          </div>
        </div>
      </div>
      <div className="px-16 py-8 border-t border-gray-800 flex justify-between text-sm text-gray-500">
        <span>User Research → Design → Prototypes</span>
        <span>Press → or Space to navigate</span>
      </div>
    </SlideLayout>
  );
}

function OverviewSlide() {
  return (
    <SlideLayout>
      <div className="flex-1 flex">
        {/* Left Column */}
        <div className="w-1/2 p-16 flex flex-col justify-center border-r border-gray-200">
          <div className="text-accent-500 text-sm font-semibold tracking-widest uppercase mb-4">
            Project Overview
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Four Integrated Modules
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            The CLEAR system provides end-to-end crisis response capabilities,
            from early detection through cash distribution. Each module is designed
            for intuitive use by humanitarian field workers.
          </p>
        </div>

        {/* Right Column - Module Cards */}
        <div className="w-1/2 p-16 bg-gray-50 grid grid-cols-2 gap-6 content-center">
          <ModuleCard
            icon={<AlertCircle className="w-6 h-6" />}
            title="Crisis Detection"
            description="Real-time monitoring and alerts"
            color="red"
          />
          <ModuleCard
            icon={<BarChart3 className="w-6 h-6" />}
            title="AI Analysis"
            description="Scenario planning and insights"
            color="blue"
          />
          <ModuleCard
            icon={<Users className="w-6 h-6" />}
            title="Operations"
            description="Team and resource management"
            color="green"
          />
          <ModuleCard
            icon={<DollarSign className="w-6 h-6" />}
            title="Cash Assistance"
            description="Beneficiary targeting"
            color="amber"
          />
        </div>
      </div>
    </SlideLayout>
  );
}

function ModuleCard({
  icon,
  title,
  description,
  color
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  color: 'red' | 'blue' | 'green' | 'amber';
}) {
  const colors = {
    red: 'border-l-red-500 bg-red-50',
    blue: 'border-l-blue-500 bg-blue-50',
    green: 'border-l-green-500 bg-green-50',
    amber: 'border-l-amber-500 bg-amber-50',
  };

  const iconColors = {
    red: 'text-red-500',
    blue: 'text-blue-500',
    green: 'text-green-500',
    amber: 'text-amber-500',
  };

  return (
    <div className={cn('p-6 border-l-4 bg-white border border-gray-200', colors[color])}>
      <div className={cn('mb-3', iconColors[color])}>{icon}</div>
      <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
      <p className="text-sm text-gray-500">{description}</p>
    </div>
  );
}

// ============================================================================
// RESEARCH SLIDES
// ============================================================================

function ResearchMethodologySlide() {
  return (
    <SlideLayout>
      <div className="flex-1 flex">
        {/* Left Column */}
        <div className="w-1/2 p-16 flex flex-col justify-center border-r border-gray-200">
          <div className="w-12 h-12 bg-purple-100 flex items-center justify-center mb-6">
            <FileSearch className="w-6 h-6 text-purple-600" />
          </div>
          <div className="text-accent-500 text-sm font-semibold tracking-widest uppercase mb-4">
            User Research
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Research Methodology
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Field practitioner interviews from NRC Sudan operations informed every design
            decision. We mapped the complete crisis response journey across 7 phases.
          </p>
        </div>

        {/* Right Column - Research Methods */}
        <div className="w-1/2 p-16 bg-gray-50 flex flex-col justify-center">
          <div className="space-y-6">
            <ResearchMethodItem
              icon={<Users className="w-5 h-5" />}
              title="Field Practitioner Interviews"
              description="Response Managers, Area Managers, Field Staff, Project Managers, Sector Specialists"
            />
            <ResearchMethodItem
              icon={<MapPin className="w-5 h-5" />}
              title="Real-World Observation"
              description="NRC Sudan operations with validated tools and workflows"
            />
            <ResearchMethodItem
              icon={<Clipboard className="w-5 h-5" />}
              title="Journey Mapping"
              description="7 crisis phases from early warning to lessons learned"
            />
            <ResearchMethodItem
              icon={<Database className="w-5 h-5" />}
              title="Systems Audit"
              description="15-20 information systems currently in use documented"
            />
          </div>
        </div>
      </div>
    </SlideLayout>
  );
}

function ResearchMethodItem({
  icon,
  title,
  description
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 bg-white border border-gray-200 flex items-center justify-center text-purple-600 flex-shrink-0">
        {icon}
      </div>
      <div>
        <div className="font-semibold text-gray-900">{title}</div>
        <div className="text-sm text-gray-500 mt-1">{description}</div>
      </div>
    </div>
  );
}

function UserPersonasSlide() {
  return (
    <SlideLayout>
      <div className="flex-1 p-16">
        <div className="text-accent-500 text-sm font-semibold tracking-widest uppercase mb-4">
          User Research
        </div>
        <h2 className="text-4xl font-bold text-gray-900 mb-12">
          Four Primary User Personas
        </h2>

        <div className="grid grid-cols-4 gap-6">
          <PersonaCard
            name="Sarah"
            role="Field Coordinator"
            skills="Basic tech"
            connectivity="2G only"
            needs="Quick reporting, offline maps"
            pain="Information overload, slow coordination"
            color="blue"
          />
          <PersonaCard
            name="Ahmed"
            role="Data Analyst"
            skills="Advanced"
            connectivity="Stable"
            needs="Aggregation, trend analysis"
            pain="Multiple dashboards, data fragmentation"
            color="green"
          />
          <PersonaCard
            name="Maria"
            role="Emergency Responder"
            skills="Intermediate"
            connectivity="Variable"
            needs="Real-time alerts, team coordination"
            pain="Notification delays, team awareness gaps"
            color="amber"
          />
          <PersonaCard
            name="Joseph"
            role="Local Partner"
            skills="Low digital literacy"
            connectivity="Minimal"
            needs="Simple interfaces, voice support"
            pain="Tool complexity, language barriers"
            color="purple"
          />
        </div>
      </div>
    </SlideLayout>
  );
}

function PersonaCard({
  name,
  role,
  skills,
  connectivity,
  needs,
  pain,
  color
}: {
  name: string;
  role: string;
  skills: string;
  connectivity: string;
  needs: string;
  pain: string;
  color: 'blue' | 'green' | 'amber' | 'purple';
}) {
  const borderColors = {
    blue: 'border-t-blue-500',
    green: 'border-t-green-500',
    amber: 'border-t-amber-500',
    purple: 'border-t-purple-500',
  };

  const iconColors = {
    blue: 'text-blue-500',
    green: 'text-green-500',
    amber: 'text-amber-500',
    purple: 'text-purple-500',
  };

  return (
    <div className={cn('bg-white border border-gray-200 border-t-4', borderColors[color])}>
      <div className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <UserCircle className={cn('w-10 h-10', iconColors[color])} />
          <div>
            <div className="font-semibold text-gray-900">{name}</div>
            <div className="text-xs text-gray-500">{role}</div>
          </div>
        </div>

        <div className="space-y-3 text-xs">
          <div className="flex items-start gap-2">
            <Smartphone className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
            <div>
              <span className="text-gray-500">Skills:</span>
              <span className="ml-1 text-gray-900">{skills}</span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Wifi className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
            <div>
              <span className="text-gray-500">Connectivity:</span>
              <span className="ml-1 text-gray-900">{connectivity}</span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Target className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
            <div>
              <span className="text-gray-500">Needs:</span>
              <span className="ml-1 text-gray-900">{needs}</span>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <XCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
            <div>
              <span className="text-gray-500">Pain:</span>
              <span className="ml-1 text-gray-900">{pain}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PainPointsSlide() {
  return (
    <SlideLayout variant="dark">
      <div className="flex-1 p-16">
        <div className="text-accent-500 text-sm font-semibold tracking-widest uppercase mb-4">
          User Research
        </div>
        <h2 className="text-4xl font-bold text-white mb-12">
          Pain Points from the Field
        </h2>

        <div className="grid grid-cols-2 gap-8">
          <QuoteCard
            quote="Who has time for IOM, ACLED, ICPAC, WMO?"
            context="Data source overload causing decision fatigue"
            icon={<Database className="w-5 h-5" />}
          />
          <QuoteCard
            quote="IOM says 500, someone says 10,000 - which is right?"
            context="Conflicting data sources with no validation"
            icon={<AlertCircle className="w-5 h-5" />}
          />
          <QuoteCard
            quote="A lot of guessing based on assumptions"
            context="Decisions made without historical data"
            icon={<Brain className="w-5 h-5" />}
          />
          <QuoteCard
            quote="How to do targeting fair AND fast?"
            context="Equity vs. speed - 'biggest issue we face'"
            icon={<Target className="w-5 h-5" />}
          />
          <QuoteCard
            quote="27 manual steps for cash"
            context="Operational inefficiency in distribution"
            icon={<Clipboard className="w-5 h-5" />}
          />
          <QuoteCard
            quote="Thousands of unused data points"
            context="Data collected but never analyzed"
            icon={<TrendingUp className="w-5 h-5" />}
          />
        </div>
      </div>
    </SlideLayout>
  );
}

function QuoteCard({
  quote,
  context,
  icon
}: {
  quote: string;
  context: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="bg-gray-800 border border-gray-700 p-6">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 bg-gray-700 flex items-center justify-center text-accent-500 flex-shrink-0">
          {icon}
        </div>
        <div>
          <div className="flex items-start gap-2 mb-2">
            <Quote className="w-4 h-4 text-accent-500 flex-shrink-0 mt-1" />
            <p className="text-white font-medium italic">{quote}</p>
          </div>
          <p className="text-sm text-gray-400">{context}</p>
        </div>
      </div>
    </div>
  );
}

function ResearchFindingsSlide() {
  return (
    <SlideLayout>
      <div className="flex-1 p-16">
        <div className="text-accent-500 text-sm font-semibold tracking-widest uppercase mb-4">
          User Research
        </div>
        <h2 className="text-4xl font-bold text-gray-900 mb-12">
          Key Research Findings
        </h2>

        <div className="grid grid-cols-4 gap-6 mb-12">
          <MetricCard
            value="15-20"
            label="Information systems navigated"
            trend="Problem"
          />
          <MetricCard
            value="40%"
            label="Response time on data gathering"
            trend="Problem"
          />
          <MetricCard
            value="72-96h"
            label="Detection to activation delay"
            trend="Problem"
          />
          <MetricCard
            value="27"
            label="Excel sheets per operation"
            trend="Problem"
          />
        </div>

        <div className="grid grid-cols-3 gap-8">
          <FindingCard
            title="Information Paralysis"
            stats={['45% basic digital literacy', '68% struggle with data interpretation', '89% need offline access']}
            icon={<Database className="w-5 h-5" />}
          />
          <FindingCard
            title="Coordination Fragmentation"
            stats={['Separate assessments on same populations', 'Duplicate efforts', 'Poor inter-agency sharing']}
            icon={<Users className="w-5 h-5" />}
          />
          <FindingCard
            title="Field Constraints"
            stats={['Average device age: 3+ years', 'Typical: 2G/3G intermittent', 'Power availability issues']}
            icon={<WifiOff className="w-5 h-5" />}
          />
        </div>
      </div>
    </SlideLayout>
  );
}

function MetricCard({
  value,
  label,
  trend
}: {
  value: string;
  label: string;
  trend: 'Problem' | 'Improvement';
}) {
  return (
    <div className="bg-gray-50 border border-gray-200 p-5 text-center">
      <div className={cn(
        'text-3xl font-bold mb-2',
        trend === 'Problem' ? 'text-red-600' : 'text-green-600'
      )}>
        {value}
      </div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}

function FindingCard({
  title,
  stats,
  icon
}: {
  title: string;
  stats: string[];
  icon: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-gray-200 p-5">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 bg-gray-100 flex items-center justify-center text-gray-600">
          {icon}
        </div>
        <h3 className="font-semibold text-gray-900">{title}</h3>
      </div>
      <ul className="space-y-2">
        {stats.map((stat, i) => (
          <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
            <span className="w-1.5 h-1.5 bg-red-500 mt-2 flex-shrink-0" />
            {stat}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ResearchToDesignSlide() {
  return (
    <SlideLayout>
      <div className="flex-1 p-16">
        <div className="text-accent-500 text-sm font-semibold tracking-widest uppercase mb-4">
          Research → Design
        </div>
        <h2 className="text-4xl font-bold text-gray-900 mb-12">
          How Research Informed Design
        </h2>

        <div className="space-y-6">
          <DesignDecisionRow
            finding="15-20 dashboards causing decision fatigue"
            design="Unified single-screen dashboard with AI synthesis"
            module="Crisis Detection"
            icon={<AlertCircle className="w-5 h-5" />}
          />
          <DesignDecisionRow
            finding="Conflicting data sources - 'which is right?'"
            design="Confidence scores + multi-source triangulation"
            module="AI Analysis"
            icon={<BarChart3 className="w-5 h-5" />}
          />
          <DesignDecisionRow
            finding="27 manual steps for cash distribution"
            design="4-step wizard with automated calculations"
            module="Cash Assistance"
            icon={<DollarSign className="w-5 h-5" />}
          />
          <DesignDecisionRow
            finding="89% need offline access"
            design="Offline-first architecture with sync"
            module="All Modules"
            icon={<WifiOff className="w-5 h-5" />}
          />
          <DesignDecisionRow
            finding="45% basic digital literacy"
            design="Simple, icon-driven UI with clear labels"
            module="Design System"
            icon={<Layout className="w-5 h-5" />}
          />
          <DesignDecisionRow
            finding="Context knowledge gaps for new responders"
            design="AI-generated historical context + patterns"
            module="Analysis + Knowledge"
            icon={<Brain className="w-5 h-5" />}
          />
        </div>
      </div>
    </SlideLayout>
  );
}

function DesignDecisionRow({
  finding,
  design,
  module,
  icon
}: {
  finding: string;
  design: string;
  module: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-6 bg-gray-50 border border-gray-200 p-4">
      <div className="w-10 h-10 bg-red-100 flex items-center justify-center flex-shrink-0">
        <XCircle className="w-5 h-5 text-red-600" />
      </div>
      <div className="flex-1">
        <div className="text-sm text-gray-600">{finding}</div>
      </div>
      <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
      <div className="w-10 h-10 bg-green-100 flex items-center justify-center flex-shrink-0">
        <CheckCircle2 className="w-5 h-5 text-green-600" />
      </div>
      <div className="flex-1">
        <div className="text-sm font-medium text-gray-900">{design}</div>
      </div>
      <div className="flex items-center gap-2 px-3 py-1.5 bg-accent-100 text-accent-700 text-xs flex-shrink-0">
        {icon}
        {module}
      </div>
    </div>
  );
}

// ============================================================================
// DESIGN SYSTEM SLIDES
// ============================================================================

function DesignSystemSlide() {
  return (
    <SlideLayout>
      <div className="flex-1 p-16">
        <div className="text-accent-500 text-sm font-semibold tracking-widest uppercase mb-4">
          Design Foundation
        </div>
        <h2 className="text-4xl font-bold text-gray-900 mb-12">
          Swiss Design System
        </h2>

        <div className="grid grid-cols-3 gap-12">
          {/* Typography */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-6">Typography</h3>
            <div className="space-y-4">
              <div>
                <div className="text-3xl font-bold text-gray-900">Headings</div>
                <div className="text-xs text-gray-400 mt-1">Inter Bold, 30px</div>
              </div>
              <div>
                <div className="text-lg text-gray-900">Body Text</div>
                <div className="text-xs text-gray-400 mt-1">Inter Regular, 16px</div>
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Labels</div>
                <div className="text-xs text-gray-400 mt-1">Inter Semibold, 11px</div>
              </div>
            </div>
          </div>

          {/* Colors */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-6">Color Palette</h3>
            <div className="space-y-3">
              <ColorSwatch color="bg-accent-500" label="Primary / Accent" hex="#2563EB" />
              <ColorSwatch color="bg-red-600" label="Critical" hex="#DC2626" />
              <ColorSwatch color="bg-amber-500" label="Warning / High" hex="#F59E0B" />
              <ColorSwatch color="bg-green-600" label="Success / Online" hex="#16A34A" />
              <ColorSwatch color="bg-gray-900" label="Text Primary" hex="#111827" />
              <ColorSwatch color="bg-gray-500" label="Text Secondary" hex="#6B7280" />
            </div>
          </div>

          {/* Principles */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-6">Design Principles</h3>
            <div className="space-y-4">
              <PrincipleItem
                title="No Border Radius"
                description="Sharp corners for a clean, professional look"
              />
              <PrincipleItem
                title="Clear Hierarchy"
                description="Visual weight guides attention"
              />
              <PrincipleItem
                title="Functional Color"
                description="Color conveys meaning, not decoration"
              />
              <PrincipleItem
                title="Data-Dense"
                description="Maximize information per screen"
              />
            </div>
          </div>
        </div>
      </div>
    </SlideLayout>
  );
}

function ColorSwatch({ color, label, hex }: { color: string; label: string; hex: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className={cn('w-8 h-8', color)} />
      <div>
        <div className="text-sm text-gray-900">{label}</div>
        <div className="text-xs text-gray-400 font-mono">{hex}</div>
      </div>
    </div>
  );
}

function PrincipleItem({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex items-start gap-3">
      <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
      <div>
        <div className="text-sm font-medium text-gray-900">{title}</div>
        <div className="text-xs text-gray-500">{description}</div>
      </div>
    </div>
  );
}

function DetectionSlide() {
  return (
    <SlideLayout>
      <div className="flex-1 flex">
        {/* Left - Info */}
        <div className="w-2/5 p-16 flex flex-col justify-center">
          <div className="w-12 h-12 bg-red-100 flex items-center justify-center mb-6">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Crisis Detection
          </h2>
          <p className="text-gray-600 mb-8">
            Real-time monitoring of multiple data sources with automatic alert generation and severity ranking.
          </p>

          <div className="space-y-4">
            <FeatureItem title="Live Alerts Feed" description="Auto-generated from 8 data sources" />
            <FeatureItem title="Interactive Map" description="Geographic visualization with filters" />
            <FeatureItem title="Alert Rules" description="Configurable trigger conditions" />
            <FeatureItem title="Manual Alerts" description="Field-reported crisis creation" />
          </div>
        </div>

        {/* Right - Screenshot Mockup */}
        <div className="w-3/5 bg-gray-100 p-8 flex items-center justify-center">
          <div className="bg-white border border-gray-200 shadow-xl w-full max-w-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-lg font-semibold">Crisis Detection</div>
                  <div className="text-xs text-gray-500">CLEAR → Detection</div>
                </div>
                <div className="flex gap-2">
                  <div className="px-3 py-1.5 bg-accent-500 text-white text-xs">Create Alert</div>
                </div>
              </div>
            </div>
            {/* Content Mock */}
            <div className="p-6">
              <div className="grid grid-cols-4 gap-4 mb-6">
                {[
                  { label: 'Active Alerts', value: '3', color: 'text-red-600' },
                  { label: 'Sources Online', value: '7/8', color: 'text-green-600' },
                  { label: 'Events Today', value: '42', color: 'text-gray-900' },
                  { label: 'Avg Response', value: '2.4h', color: 'text-gray-900' },
                ].map((stat, i) => (
                  <div key={i} className="bg-gray-50 p-4 border border-gray-200">
                    <div className="text-xs text-gray-500 uppercase">{stat.label}</div>
                    <div className={cn('text-2xl font-bold', stat.color)}>{stat.value}</div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="border border-gray-200 p-4">
                  <div className="text-sm font-semibold mb-3">Active Alerts</div>
                  <div className="space-y-2">
                    <AlertMock severity="critical" title="Cholera Outbreak" />
                    <AlertMock severity="high" title="Flooding Risk" />
                    <AlertMock severity="medium" title="Drought Monitor" />
                  </div>
                </div>
                <div className="border border-gray-200 p-4 bg-gray-50">
                  <div className="text-sm font-semibold mb-3">Crisis Map</div>
                  <div className="h-32 bg-gray-200 flex items-center justify-center text-gray-400 text-xs">
                    [Interactive Map]
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SlideLayout>
  );
}

function AlertMock({ severity, title }: { severity: 'critical' | 'high' | 'medium'; title: string }) {
  const colors = {
    critical: 'bg-red-600',
    high: 'bg-amber-500',
    medium: 'bg-amber-400',
  };

  return (
    <div className="flex items-center gap-3 p-2 bg-white border border-gray-200">
      <div className={cn('w-1 h-8', colors[severity])} />
      <div>
        <div className="text-xs font-medium">{title}</div>
        <div className="text-[10px] text-gray-500">Somali Region</div>
      </div>
    </div>
  );
}

function FeatureItem({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-1.5 h-1.5 bg-accent-500 mt-2 flex-shrink-0" />
      <div>
        <div className="text-sm font-medium text-gray-900">{title}</div>
        <div className="text-xs text-gray-500">{description}</div>
      </div>
    </div>
  );
}

function AnalysisSlide() {
  return (
    <SlideLayout>
      <div className="flex-1 flex">
        {/* Left - Screenshot Mockup */}
        <div className="w-3/5 bg-gray-100 p-8 flex items-center justify-center">
          <div className="bg-white border border-gray-200 shadow-xl w-full max-w-2xl">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="text-lg font-semibold">AI-Powered Analysis</div>
            </div>
            <div className="p-6">
              {/* AI Banner */}
              <div className="bg-gradient-to-r from-blue-50 to-sky-50 border border-accent-500 p-4 mb-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-accent-500 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-xs text-accent-500 font-semibold uppercase">Latest AI Analysis</div>
                    <div className="font-semibold text-gray-900">Cholera Outbreak Situation Report</div>
                    <div className="text-xs text-gray-500 mt-1">847 data points • 94% confidence</div>
                  </div>
                </div>
              </div>
              {/* Scenarios */}
              <div className="grid grid-cols-3 gap-4">
                <ScenarioMock name="Best Case" likelihood="25%" color="green" />
                <ScenarioMock name="Most Likely" likelihood="55%" color="blue" highlight />
                <ScenarioMock name="Worst Case" likelihood="20%" color="red" />
              </div>
            </div>
          </div>
        </div>

        {/* Right - Info */}
        <div className="w-2/5 p-16 flex flex-col justify-center">
          <div className="w-12 h-12 bg-blue-100 flex items-center justify-center mb-6">
            <BarChart3 className="w-6 h-6 text-blue-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            AI-Powered Analysis
          </h2>
          <p className="text-gray-600 mb-8">
            Machine learning-driven situation reports, scenario planning, and actionable insights.
          </p>

          <div className="space-y-4">
            <FeatureItem title="Situation Reports" description="AI-generated crisis assessments" />
            <FeatureItem title="Scenario Planning" description="Best/likely/worst case modeling" />
            <FeatureItem title="Historical Analysis" description="Learn from past responses" />
            <FeatureItem title="Pattern Detection" description="Automated insight generation" />
          </div>
        </div>
      </div>
    </SlideLayout>
  );
}

function ScenarioMock({
  name,
  likelihood,
  color,
  highlight = false
}: {
  name: string;
  likelihood: string;
  color: 'green' | 'blue' | 'red';
  highlight?: boolean;
}) {
  const colors = {
    green: 'bg-green-50 border-green-200 text-green-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    red: 'bg-red-50 border-red-200 text-red-700',
  };

  return (
    <div className={cn(
      'p-4 border',
      colors[color],
      highlight && 'border-l-4 border-l-accent-500'
    )}>
      <div className="text-2xl font-bold">{likelihood}</div>
      <div className="text-sm font-medium">{name}</div>
    </div>
  );
}

function OperationsSlide() {
  return (
    <SlideLayout>
      <div className="flex-1 flex">
        {/* Left - Info */}
        <div className="w-2/5 p-16 flex flex-col justify-center">
          <div className="w-12 h-12 bg-green-100 flex items-center justify-center mb-6">
            <Users className="w-6 h-6 text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Operations Center
          </h2>
          <p className="text-gray-600 mb-8">
            Coordinate field teams, manage resources, and track partner organizations in real-time.
          </p>

          <div className="space-y-4">
            <FeatureItem title="Team Deployment" description="Deploy and track field teams" />
            <FeatureItem title="Resource Management" description="Monitor supplies and equipment" />
            <FeatureItem title="Partner Coordination" description="Multi-agency collaboration" />
            <FeatureItem title="Real-time Status" description="Live check-ins and updates" />
          </div>
        </div>

        {/* Right - Screenshot Mockup */}
        <div className="w-3/5 bg-gray-100 p-8 flex items-center justify-center">
          <div className="bg-white border border-gray-200 shadow-xl w-full max-w-2xl">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="text-lg font-semibold">Operations Center</div>
            </div>
            <div className="p-6">
              {/* Stats */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <StatMock label="Operations" value="2" />
                <StatMock label="Teams" value="12" />
                <StatMock label="Staff" value="67" />
                <StatMock label="Partners" value="8" />
              </div>
              {/* Teams Table Mock */}
              <div className="border border-gray-200">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase">
                  Field Teams
                </div>
                <div className="divide-y divide-gray-200">
                  <TeamRowMock name="WASH Team Alpha" status="Active" location="Jijiga" />
                  <TeamRowMock name="Health Team Beta" status="En Route" location="Kebridehar" />
                  <TeamRowMock name="Assessment Team" status="Active" location="Gode" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SlideLayout>
  );
}

function StatMock({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-gray-50 p-3 border border-gray-200 text-center">
      <div className="text-xs text-gray-500 uppercase">{label}</div>
      <div className="text-xl font-bold text-gray-900">{value}</div>
    </div>
  );
}

function TeamRowMock({ name, status, location }: { name: string; status: string; location: string }) {
  return (
    <div className="px-4 py-3 flex items-center justify-between">
      <div>
        <div className="text-sm font-medium">{name}</div>
        <div className="text-xs text-gray-500">{location}</div>
      </div>
      <div className={cn(
        'text-xs px-2 py-0.5',
        status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
      )}>
        {status}
      </div>
    </div>
  );
}

function CashAssistanceSlide() {
  return (
    <SlideLayout>
      <div className="flex-1 flex">
        {/* Left - Screenshot Mockup */}
        <div className="w-3/5 bg-gray-100 p-8 flex items-center justify-center">
          <div className="bg-white border border-gray-200 shadow-xl w-full max-w-2xl">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="text-lg font-semibold">Cash Assistance</div>
            </div>
            <div className="p-6">
              {/* Stats */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <StatMock label="Affected" value="185K" />
                <StatMock label="Transfer" value="580 Birr" />
                <StatMock label="Eligible HH" value="5,000" />
                <StatMock label="Budget" value="2.9M" />
              </div>
              {/* Map + Demographics */}
              <div className="grid grid-cols-2 gap-4">
                <div className="border border-gray-200 p-4">
                  <div className="text-sm font-semibold mb-3">Beneficiary Map</div>
                  <div className="h-24 bg-gray-100 flex items-center justify-center text-xs text-gray-400">
                    [Interactive targeting map]
                  </div>
                </div>
                <div className="border border-gray-200 p-4">
                  <div className="text-sm font-semibold mb-3">Demographics</div>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Female-headed HH</span>
                      <span className="font-medium">172</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">With disability</span>
                      <span className="font-medium">103</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Chronic illness</span>
                      <span className="font-medium">154</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right - Info */}
        <div className="w-2/5 p-16 flex flex-col justify-center">
          <div className="w-12 h-12 bg-amber-100 flex items-center justify-center mb-6">
            <DollarSign className="w-6 h-6 text-amber-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Cash Assistance
          </h2>
          <p className="text-gray-600 mb-8">
            AI-powered beneficiary targeting with demographic analysis and multi-step distribution wizard.
          </p>

          <div className="space-y-4">
            <FeatureItem title="Geographic Targeting" description="Map-based location selection" />
            <FeatureItem title="Vulnerability Analysis" description="Demographic breakdown" />
            <FeatureItem title="Market Assessment" description="Feasibility indicators" />
            <FeatureItem title="Distribution Wizard" description="4-step creation flow" />
          </div>
        </div>
      </div>
    </SlideLayout>
  );
}

function ComponentsSlide() {
  return (
    <SlideLayout>
      <div className="flex-1 p-16">
        <div className="text-accent-500 text-sm font-semibold tracking-widest uppercase mb-4">
          UI Component Library
        </div>
        <h2 className="text-4xl font-bold text-gray-900 mb-12">
          30+ Reusable Components
        </h2>

        <div className="grid grid-cols-4 gap-6">
          <ComponentCategory
            icon={<Layers className="w-5 h-5" />}
            title="Layout"
            items={['WireframeLayout', 'WireframeHeader', 'WireframeCard', 'WireframeTabs']}
          />
          <ComponentCategory
            icon={<Box className="w-5 h-5" />}
            title="Data Display"
            items={['WireframeTable', 'StatCard', 'StatusIndicator', 'CrisisBadge']}
          />
          <ComponentCategory
            icon={<Zap className="w-5 h-5" />}
            title="Loading States"
            items={['Skeleton', 'SkeletonTable', 'LoadingSpinner', 'LoadingOverlay']}
          />
          <ComponentCategory
            icon={<Layout className="w-5 h-5" />}
            title="Overlays"
            items={['Modal', 'ConfirmDialog', 'ToastProvider', 'ToastItem']}
          />
          <ComponentCategory
            icon={<Target className="w-5 h-5" />}
            title="Forms"
            items={['Input', 'Select', 'TextArea', 'ValidatedInput']}
          />
          <ComponentCategory
            icon={<MessageSquare className="w-5 h-5" />}
            title="Feedback"
            items={['EmptyState', 'InlineAlert', 'Toast', 'ErrorBoundary']}
          />
          <ComponentCategory
            icon={<HelpCircle className="w-5 h-5" />}
            title="Help"
            items={['Tooltip', 'HelpTooltip', 'LabelWithHelp', 'Breadcrumbs']}
          />
          <ComponentCategory
            icon={<RefreshCw className="w-5 h-5" />}
            title="Data Refresh"
            items={['RefreshBadge', 'DataRefreshIndicator', 'useDataRefresh']}
          />
        </div>
      </div>
    </SlideLayout>
  );
}

function ComponentCategory({
  icon,
  title,
  items
}: {
  icon: React.ReactNode;
  title: string;
  items: string[];
}) {
  return (
    <div className="bg-gray-50 border border-gray-200 p-5">
      <div className="flex items-center gap-2 mb-4 text-gray-700">
        {icon}
        <span className="font-semibold">{title}</span>
      </div>
      <div className="space-y-1.5">
        {items.map((item, i) => (
          <div key={i} className="text-sm text-gray-600 font-mono">{item}</div>
        ))}
      </div>
    </div>
  );
}

function FeaturesSlide() {
  return (
    <SlideLayout>
      <div className="flex-1 p-16">
        <div className="text-accent-500 text-sm font-semibold tracking-widest uppercase mb-4">
          User Testing Features
        </div>
        <h2 className="text-4xl font-bold text-gray-900 mb-12">
          Implementation Priorities
        </h2>

        <div className="grid grid-cols-3 gap-8">
          {/* Quick Wins */}
          <div>
            <div className="flex items-center gap-2 mb-6">
              <div className="w-8 h-8 bg-green-100 flex items-center justify-center">
                <Zap className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <div className="font-semibold text-gray-900">Quick Wins</div>
                <div className="text-xs text-gray-500">Completed</div>
              </div>
            </div>
            <div className="space-y-3">
              <FeatureCheck title="Keyboard Esc to close" done />
              <FeatureCheck title="Cancel confirmations" done />
              <FeatureCheck title="Required field indicators" done />
              <FeatureCheck title="Empty state components" done />
              <FeatureCheck title="Breadcrumb navigation" done />
            </div>
          </div>

          {/* Priority 1 */}
          <div>
            <div className="flex items-center gap-2 mb-6">
              <div className="w-8 h-8 bg-blue-100 flex items-center justify-center">
                <Target className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <div className="font-semibold text-gray-900">Priority 1</div>
                <div className="text-xs text-gray-500">Critical for Testing</div>
              </div>
            </div>
            <div className="space-y-3">
              <FeatureCheck title="Form validation with errors" done />
              <FeatureCheck title="Confirmation dialogs" done />
              <FeatureCheck title="Empty states for all views" done />
            </div>
          </div>

          {/* Priority 2 */}
          <div>
            <div className="flex items-center gap-2 mb-6">
              <div className="w-8 h-8 bg-amber-100 flex items-center justify-center">
                <Lightbulb className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <div className="font-semibold text-gray-900">Priority 2</div>
                <div className="text-xs text-gray-500">Enhanced UX</div>
              </div>
            </div>
            <div className="space-y-3">
              <FeatureCheck title="Contextual help tooltips" done />
              <FeatureCheck title="Data refresh indicators" done />
              <FeatureCheck title="Refresh badges" done />
            </div>
          </div>
        </div>
      </div>
    </SlideLayout>
  );
}

function FeatureCheck({ title, done = false }: { title: string; done?: boolean }) {
  return (
    <div className="flex items-center gap-3">
      {done ? (
        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
      ) : (
        <Circle className="w-4 h-4 text-gray-300 flex-shrink-0" />
      )}
      <span className={cn('text-sm', done ? 'text-gray-900' : 'text-gray-400')}>{title}</span>
    </div>
  );
}

function WorkflowsSlide() {
  return (
    <SlideLayout>
      <div className="flex-1 p-16">
        <div className="text-accent-500 text-sm font-semibold tracking-widest uppercase mb-4">
          User Journeys
        </div>
        <h2 className="text-4xl font-bold text-gray-900 mb-12">
          Key Workflows
        </h2>

        <div className="grid grid-cols-2 gap-8">
          <WorkflowCard
            title="Crisis Response"
            steps={[
              'Detect alert in feed',
              'Review on map',
              'Acknowledge alert',
              'Generate AI analysis',
              'Deploy field team',
              'Request resources',
            ]}
          />
          <WorkflowCard
            title="Cash Distribution"
            steps={[
              'Select target locations',
              'Review demographics',
              'Check market feasibility',
              'Configure transfer details',
              'Set targeting criteria',
              'Submit distribution',
            ]}
          />
        </div>
      </div>
    </SlideLayout>
  );
}

function WorkflowCard({ title, steps }: { title: string; steps: string[] }) {
  return (
    <div className="bg-gray-50 border border-gray-200 p-6">
      <h3 className="font-semibold text-gray-900 mb-6">{title}</h3>
      <div className="space-y-4">
        {steps.map((step, index) => (
          <div key={index} className="flex items-center gap-4">
            <div className="w-8 h-8 bg-white border border-gray-200 flex items-center justify-center text-sm font-semibold text-gray-500">
              {index + 1}
            </div>
            <div className="flex-1 text-sm text-gray-700">{step}</div>
            {index < steps.length - 1 && (
              <ArrowRight className="w-4 h-4 text-gray-300" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function TestingSlide() {
  return (
    <SlideLayout>
      <div className="flex-1 p-16">
        <div className="text-accent-500 text-sm font-semibold tracking-widest uppercase mb-4">
          Quality Assurance
        </div>
        <h2 className="text-4xl font-bold text-gray-900 mb-12">
          User Testing Scenarios
        </h2>

        <div className="grid grid-cols-3 gap-6">
          <TestScenario
            number={1}
            title="New Crisis Response"
            description="End-to-end response from alert detection to team deployment"
            duration="10 min"
          />
          <TestScenario
            number={2}
            title="Data Refresh"
            description="Test refresh functionality across all data views"
            duration="3 min"
          />
          <TestScenario
            number={3}
            title="Form Validation"
            description="Verify error states and validation messages"
            duration="5 min"
          />
          <TestScenario
            number={4}
            title="Empty States"
            description="Search for non-existent data, verify guidance"
            duration="3 min"
          />
          <TestScenario
            number={5}
            title="Tutorial Walkthrough"
            description="Complete interactive tutorial for each page"
            duration="15 min"
          />
          <TestScenario
            number={6}
            title="Cash Distribution"
            description="Complete 4-step distribution wizard"
            duration="8 min"
          />
        </div>
      </div>
    </SlideLayout>
  );
}

function TestScenario({
  number,
  title,
  description,
  duration
}: {
  number: number;
  title: string;
  description: string;
  duration: string;
}) {
  return (
    <div className="bg-gray-50 border border-gray-200 p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-8 h-8 bg-accent-500 text-white flex items-center justify-center text-sm font-bold">
          {number}
        </div>
        <div className="flex-1">
          <div className="font-semibold text-gray-900">{title}</div>
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <Clock className="w-3 h-3" />
          {duration}
        </div>
      </div>
      <p className="text-sm text-gray-600">{description}</p>
    </div>
  );
}

// ============================================================================
// STAKEHOLDER SLIDES
// ============================================================================

function ImpactROISlide() {
  return (
    <SlideLayout>
      <div className="flex-1 p-16">
        <div className="text-accent-500 text-sm font-semibold tracking-widest uppercase mb-4">
          Business Case
        </div>
        <h2 className="text-4xl font-bold text-gray-900 mb-12">
          Projected Impact & ROI
        </h2>

        <div className="grid grid-cols-2 gap-12">
          {/* Current State Problems */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-red-600" />
              Current State Costs
            </h3>
            <div className="space-y-4">
              <ImpactMetric
                label="Response time on data gathering"
                current="40%"
                description="of total response time lost to manual data collection"
                variant="negative"
              />
              <ImpactMetric
                label="Detection to activation delay"
                current="72-96h"
                description="critical window where lives are at risk"
                variant="negative"
              />
              <ImpactMetric
                label="Resource misallocation"
                current="30%"
                description="of resources misallocated due to poor information"
                variant="negative"
              />
              <ImpactMetric
                label="Duplicate assessments"
                current="40%"
                description="of organizations conduct separate assessments"
                variant="negative"
              />
            </div>
          </div>

          {/* Projected Improvements */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              Projected Improvements
            </h3>
            <div className="space-y-4">
              <ImpactMetric
                label="Response timeline compression"
                current="60%"
                description="faster crisis response activation"
                variant="positive"
              />
              <ImpactMetric
                label="Information processing efficiency"
                current="75%"
                description="reduction in time spent gathering data"
                variant="positive"
              />
              <ImpactMetric
                label="Decision accuracy"
                current="65% → 95%"
                description="improvement with AI-assisted analysis"
                variant="positive"
              />
              <ImpactMetric
                label="Coordination effectiveness"
                current="80%"
                description="reduction in duplicate assessments"
                variant="positive"
              />
            </div>
          </div>
        </div>
      </div>
    </SlideLayout>
  );
}

function ImpactMetric({
  label,
  current,
  description,
  variant
}: {
  label: string;
  current: string;
  description: string;
  variant: 'positive' | 'negative';
}) {
  return (
    <div className={cn(
      'p-4 border-l-4',
      variant === 'positive' ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'
    )}>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-sm text-gray-600">{label}</span>
        <span className={cn(
          'text-xl font-bold',
          variant === 'positive' ? 'text-green-600' : 'text-red-600'
        )}>{current}</span>
      </div>
      <p className="text-xs text-gray-500">{description}</p>
    </div>
  );
}

function CurrentVsFutureSlide() {
  return (
    <SlideLayout>
      <div className="flex-1 p-16">
        <div className="text-accent-500 text-sm font-semibold tracking-widest uppercase mb-4">
          Transformation
        </div>
        <h2 className="text-4xl font-bold text-gray-900 mb-12">
          Current State vs. CLEAR System
        </h2>

        <div className="grid grid-cols-2 gap-8">
          {/* Current State */}
          <div className="bg-red-50 border border-red-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-red-100 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Current State</h3>
            </div>
            <div className="space-y-4">
              <ComparisonItem text="15-20 separate dashboards and data sources" negative />
              <ComparisonItem text="27 Excel sheets per operation" negative />
              <ComparisonItem text="Manual data gathering from multiple systems" negative />
              <ComparisonItem text="WhatsApp groups for coordination" negative />
              <ComparisonItem text="8+ hours to compile situation reports" negative />
              <ComparisonItem text="No confidence scores on data" negative />
              <ComparisonItem text="Decision-making based on assumptions" negative />
              <ComparisonItem text="No historical pattern recognition" negative />
            </div>
          </div>

          {/* Future State */}
          <div className="bg-green-50 border border-green-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">With CLEAR</h3>
            </div>
            <div className="space-y-4">
              <ComparisonItem text="Single unified dashboard with all data" positive />
              <ComparisonItem text="4-step automated workflows" positive />
              <ComparisonItem text="AI aggregates data automatically" positive />
              <ComparisonItem text="Integrated team coordination tools" positive />
              <ComparisonItem text="AI-generated reports in minutes" positive />
              <ComparisonItem text="Confidence scores + source triangulation" positive />
              <ComparisonItem text="Data-driven decisions with AI insights" positive />
              <ComparisonItem text="Historical context for every crisis" positive />
            </div>
          </div>
        </div>
      </div>
    </SlideLayout>
  );
}

function ComparisonItem({ text, positive = false, negative = false }: { text: string; positive?: boolean; negative?: boolean }) {
  return (
    <div className="flex items-start gap-3">
      {positive && <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />}
      {negative && <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />}
      <span className="text-sm text-gray-700">{text}</span>
    </div>
  );
}

function RiskMitigationSlide() {
  return (
    <SlideLayout>
      <div className="flex-1 p-16">
        <div className="text-accent-500 text-sm font-semibold tracking-widest uppercase mb-4">
          Risk Management
        </div>
        <h2 className="text-4xl font-bold text-gray-900 mb-10">
          Risks & Mitigations
        </h2>

        <div className="grid grid-cols-2 gap-6">
          {/* Technical & Field Risks */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Technical & Field Risks</h3>
            <div className="space-y-4">
              <RiskItem
                risk="Low connectivity in field"
                mitigation="Offline-first architecture with background sync"
                severity="high"
              />
              <RiskItem
                risk="Device limitations (old hardware)"
                mitigation="Progressive enhancement, lightweight components"
                severity="medium"
              />
              <RiskItem
                risk="Data security concerns"
                mitigation="End-to-end encryption, local data storage options"
                severity="high"
              />
              <RiskItem
                risk="Integration with existing systems"
                mitigation="API-first design, standard humanitarian data formats"
                severity="medium"
              />
            </div>
          </div>

          {/* Adoption & Usefulness Risks */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Adoption & Usefulness Risks</h3>
            <div className="space-y-4">
              <RiskItem
                risk="Low user adoption"
                mitigation="Research-driven design, iterative user testing, phased rollout"
                severity="high"
              />
              <RiskItem
                risk="Feature doesn't match actual needs"
                mitigation="Continuous field validation, feedback loops built-in"
                severity="high"
              />
              <RiskItem
                risk="Training and change management"
                mitigation="Embedded tutorials, role-based training modules"
                severity="medium"
              />
              <RiskItem
                risk="AI recommendations not trusted"
                mitigation="Transparent confidence scores, explainable AI, human override"
                severity="medium"
              />
              <RiskItem
                risk="Tool complexity vs. simplicity"
                mitigation="Progressive disclosure, persona-based interfaces"
                severity="medium"
              />
              <RiskItem
                risk="Multi-language support gaps"
                mitigation="i18n framework, local language prioritization"
                severity="low"
              />
            </div>
          </div>
        </div>
      </div>
    </SlideLayout>
  );
}

function RiskItem({
  risk,
  mitigation,
  severity
}: {
  risk: string;
  mitigation: string;
  severity: 'high' | 'medium' | 'low';
}) {
  const severityColors = {
    high: 'bg-red-100 text-red-700 border-red-200',
    medium: 'bg-amber-100 text-amber-700 border-amber-200',
    low: 'bg-green-100 text-green-700 border-green-200',
  };

  return (
    <div className="bg-white border border-gray-200 p-4">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <span className="text-sm font-medium text-gray-900">{risk}</span>
        </div>
        <span className={cn('text-[10px] px-2 py-0.5 border', severityColors[severity])}>
          {severity.toUpperCase()}
        </span>
      </div>
      <div className="flex items-start gap-2 ml-6">
        <Shield className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
        <span className="text-xs text-gray-600">{mitigation}</span>
      </div>
    </div>
  );
}

function ImplementationRoadmapSlide() {
  return (
    <SlideLayout>
      <div className="flex-1 p-16">
        <div className="text-accent-500 text-sm font-semibold tracking-widest uppercase mb-4">
          Implementation
        </div>
        <h2 className="text-4xl font-bold text-gray-900 mb-12">
          Phased Roadmap
        </h2>

        <div className="grid grid-cols-4 gap-6">
          <PhaseCard
            phase={1}
            title="Prototype"
            status="current"
            items={[
              'User research synthesis',
              'Wireframe prototypes',
              'User testing with field workers',
              'Design validation'
            ]}
          />
          <PhaseCard
            phase={2}
            title="Pilot Development"
            status="next"
            items={[
              'Core module development',
              'Real API integration',
              'Offline capability',
              'Security implementation'
            ]}
          />
          <PhaseCard
            phase={3}
            title="Field Pilot"
            status="future"
            items={[
              'NRC Sudan deployment',
              'Field testing with 2-3 teams',
              'Feedback collection',
              'Iterative improvements'
            ]}
          />
          <PhaseCard
            phase={4}
            title="Scale"
            status="future"
            items={[
              'Multi-region deployment',
              'Partner organization onboarding',
              'Advanced AI features',
              'Full offline support'
            ]}
          />
        </div>

        <div className="mt-12 flex items-center justify-between px-8">
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 bg-accent-500" />
            <span className="text-sm text-gray-600">Current Phase</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 bg-amber-500" />
            <span className="text-sm text-gray-600">Next Phase</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 bg-gray-300" />
            <span className="text-sm text-gray-600">Future Phases</span>
          </div>
        </div>
      </div>
    </SlideLayout>
  );
}

function PhaseCard({
  phase,
  title,
  status,
  items
}: {
  phase: number;
  title: string;
  status: 'current' | 'next' | 'future';
  items: string[];
}) {
  const statusColors = {
    current: 'border-t-accent-500 bg-accent-50',
    next: 'border-t-amber-500 bg-amber-50',
    future: 'border-t-gray-300 bg-gray-50',
  };

  const badgeColors = {
    current: 'bg-accent-500 text-white',
    next: 'bg-amber-500 text-white',
    future: 'bg-gray-300 text-gray-700',
  };

  return (
    <div className={cn('border border-gray-200 border-t-4', statusColors[status])}>
      <div className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className={cn('w-8 h-8 flex items-center justify-center text-sm font-bold', badgeColors[status])}>
            {phase}
          </div>
          <div className="font-semibold text-gray-900">{title}</div>
        </div>
        <ul className="space-y-2">
          {items.map((item, i) => (
            <li key={i} className="text-xs text-gray-600 flex items-start gap-2">
              <span className="w-1 h-1 bg-gray-400 mt-1.5 flex-shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function PilotProgramSlide() {
  return (
    <SlideLayout>
      <div className="flex-1 flex">
        {/* Left Column */}
        <div className="w-1/2 p-16 flex flex-col justify-center border-r border-gray-200">
          <div className="w-12 h-12 bg-amber-100 flex items-center justify-center mb-6">
            <Rocket className="w-6 h-6 text-amber-600" />
          </div>
          <div className="text-accent-500 text-sm font-semibold tracking-widest uppercase mb-4">
            Field Validation
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Pilot Program Plan
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Validate CLEAR with real humanitarian operations before scaling.
            NRC Sudan provides the ideal testing environment given our research foundation.
          </p>
        </div>

        {/* Right Column */}
        <div className="w-1/2 p-16 bg-gray-50 flex flex-col justify-center">
          <div className="space-y-6">
            <PilotItem
              icon={<MapPin className="w-5 h-5" />}
              title="Pilot Location"
              description="NRC Sudan - Darfur & Eastern regions where research was conducted"
            />
            <PilotItem
              icon={<Users className="w-5 h-5" />}
              title="Pilot Teams"
              description="2-3 field response teams, 15-20 total users across roles"
            />
            <PilotItem
              icon={<Calendar className="w-5 h-5" />}
              title="Duration"
              description="Initial pilot assessment, iterative improvement cycles"
            />
            <PilotItem
              icon={<Target className="w-5 h-5" />}
              title="Success Criteria"
              description="Response time reduction, user satisfaction >80%, data accuracy improvement"
            />
            <PilotItem
              icon={<FileText className="w-5 h-5" />}
              title="Evaluation"
              description="Weekly feedback sessions, usage analytics, impact assessment"
            />
          </div>
        </div>
      </div>
    </SlideLayout>
  );
}

function PilotItem({
  icon,
  title,
  description
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 bg-white border border-gray-200 flex items-center justify-center text-amber-600 flex-shrink-0">
        {icon}
      </div>
      <div>
        <div className="font-semibold text-gray-900">{title}</div>
        <div className="text-sm text-gray-500 mt-1">{description}</div>
      </div>
    </div>
  );
}

function TechnicalArchitectureSlide() {
  return (
    <SlideLayout>
      <div className="flex-1 p-16">
        <div className="text-accent-500 text-sm font-semibold tracking-widest uppercase mb-4">
          Technical Overview
        </div>
        <h2 className="text-4xl font-bold text-gray-900 mb-12">
          System Architecture
        </h2>

        <div className="grid grid-cols-3 gap-8">
          {/* Data Layer */}
          <div className="bg-blue-50 border border-blue-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Database className="w-6 h-6 text-blue-600" />
              <h3 className="font-semibold text-gray-900">Data Layer</h3>
            </div>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 mt-2 flex-shrink-0" />
                IOM DTM integration
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 mt-2 flex-shrink-0" />
                ACLED conflict data
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 mt-2 flex-shrink-0" />
                Weather/climate (ICPAC, WMO)
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 mt-2 flex-shrink-0" />
                Field data collection (KoboToolbox)
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 mt-2 flex-shrink-0" />
                Local offline storage
              </li>
            </ul>
          </div>

          {/* Processing Layer */}
          <div className="bg-purple-50 border border-purple-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Brain className="w-6 h-6 text-purple-600" />
              <h3 className="font-semibold text-gray-900">AI Processing</h3>
            </div>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-purple-500 mt-2 flex-shrink-0" />
                Multi-source aggregation
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-purple-500 mt-2 flex-shrink-0" />
                Confidence scoring
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-purple-500 mt-2 flex-shrink-0" />
                Pattern recognition
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-purple-500 mt-2 flex-shrink-0" />
                Scenario modeling
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-purple-500 mt-2 flex-shrink-0" />
                Report generation
              </li>
            </ul>
          </div>

          {/* Application Layer */}
          <div className="bg-green-50 border border-green-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Layout className="w-6 h-6 text-green-600" />
              <h3 className="font-semibold text-gray-900">Application</h3>
            </div>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-green-500 mt-2 flex-shrink-0" />
                React + TypeScript frontend
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-green-500 mt-2 flex-shrink-0" />
                Progressive Web App (PWA)
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-green-500 mt-2 flex-shrink-0" />
                Offline-first architecture
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-green-500 mt-2 flex-shrink-0" />
                Role-based access control
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-green-500 mt-2 flex-shrink-0" />
                End-to-end encryption
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-8 p-4 bg-gray-100 border border-gray-200 flex items-center justify-center gap-8">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Lock className="w-4 h-4 text-gray-500" />
            <span>Security-first design</span>
          </div>
          <div className="w-px h-4 bg-gray-300" />
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Cloud className="w-4 h-4 text-gray-500" />
            <span>Cloud-native + offline capable</span>
          </div>
          <div className="w-px h-4 bg-gray-300" />
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Globe className="w-4 h-4 text-gray-500" />
            <span>Multi-region deployment ready</span>
          </div>
        </div>
      </div>
    </SlideLayout>
  );
}

function BudgetResourcesSlide() {
  return (
    <SlideLayout>
      <div className="flex-1 p-16">
        <div className="text-accent-500 text-sm font-semibold tracking-widest uppercase mb-4">
          Investment
        </div>
        <h2 className="text-4xl font-bold text-gray-900 mb-12">
          Budget & Resource Requirements
        </h2>

        <div className="grid grid-cols-3 gap-8">
          {/* Development */}
          <BudgetCard
            title="Development"
            icon={<Server className="w-5 h-5" />}
            items={[
              { label: 'Frontend Development', detail: 'React/TypeScript team' },
              { label: 'Backend & API', detail: 'Node.js, integrations' },
              { label: 'AI/ML Development', detail: 'Model training, inference' },
              { label: 'DevOps & Infrastructure', detail: 'Cloud setup, CI/CD' },
            ]}
            color="blue"
          />

          {/* Operations */}
          <BudgetCard
            title="Operations"
            icon={<Building className="w-5 h-5" />}
            items={[
              { label: 'Cloud Hosting', detail: 'AWS/Azure with offline sync' },
              { label: 'Data Services', detail: 'API subscriptions' },
              { label: 'Security & Compliance', detail: 'Audits, encryption' },
              { label: 'Support & Maintenance', detail: 'Ongoing operations' },
            ]}
            color="green"
          />

          {/* Field Deployment */}
          <BudgetCard
            title="Field Deployment"
            icon={<Users className="w-5 h-5" />}
            items={[
              { label: 'Training Development', detail: 'Multi-language modules' },
              { label: 'Field Support', detail: 'On-site deployment team' },
              { label: 'User Testing', detail: 'Iterative feedback cycles' },
              { label: 'Change Management', detail: 'Adoption support' },
            ]}
            color="amber"
          />
        </div>

        <div className="mt-10 bg-gray-50 border border-gray-200 p-6">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Key Success Factors</h3>
          <div className="grid grid-cols-3 gap-6">
            <div className="flex items-center gap-3">
              <CheckSquare className="w-5 h-5 text-green-600" />
              <span className="text-sm text-gray-700">Multi-org ownership from Day 1</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckSquare className="w-5 h-5 text-green-600" />
              <span className="text-sm text-gray-700">Dedicated field liaison</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckSquare className="w-5 h-5 text-green-600" />
              <span className="text-sm text-gray-700">Iterative user validation</span>
            </div>
          </div>
        </div>
      </div>
    </SlideLayout>
  );
}

function BudgetCard({
  title,
  icon,
  items,
  color
}: {
  title: string;
  icon: React.ReactNode;
  items: { label: string; detail: string }[];
  color: 'blue' | 'green' | 'amber';
}) {
  const colors = {
    blue: 'bg-blue-50 border-blue-200 text-blue-600',
    green: 'bg-green-50 border-green-200 text-green-600',
    amber: 'bg-amber-50 border-amber-200 text-amber-600',
  };

  const iconColors = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    amber: 'bg-amber-100 text-amber-600',
  };

  return (
    <div className={cn('border p-6', colors[color])}>
      <div className="flex items-center gap-3 mb-5">
        <div className={cn('w-10 h-10 flex items-center justify-center', iconColors[color])}>
          {icon}
        </div>
        <h3 className="font-semibold text-gray-900">{title}</h3>
      </div>
      <div className="space-y-3">
        {items.map((item, i) => (
          <div key={i}>
            <div className="text-sm font-medium text-gray-900">{item.label}</div>
            <div className="text-xs text-gray-500">{item.detail}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProgressDashboardSlide() {
  const categories = [
    {
      name: 'Research & Discovery',
      color: 'purple',
      items: [
        { name: 'Field practitioner interviews', status: 'complete' as const },
        { name: 'User persona development', status: 'complete' as const },
        { name: 'Pain point analysis', status: 'complete' as const },
        { name: 'Journey mapping (7 phases)', status: 'complete' as const },
        { name: 'Systems audit', status: 'complete' as const },
      ]
    },
    {
      name: 'Design & Prototypes',
      color: 'blue',
      items: [
        { name: 'Design system (Swiss)', status: 'complete' as const },
        { name: 'Crisis Detection module', status: 'complete' as const },
        { name: 'AI Analysis module', status: 'complete' as const },
        { name: 'Operations Center module', status: 'complete' as const },
        { name: 'Cash Assistance module', status: 'complete' as const },
        { name: 'Interactive map views', status: 'complete' as const },
        { name: '30+ UI components', status: 'complete' as const },
      ]
    },
    {
      name: 'User Testing Readiness',
      color: 'green',
      items: [
        { name: 'Loading states & skeletons', status: 'complete' as const },
        { name: 'Form validation', status: 'complete' as const },
        { name: 'Empty states', status: 'complete' as const },
        { name: 'Contextual tooltips', status: 'complete' as const },
        { name: 'Tutorial walkthrough', status: 'complete' as const },
        { name: 'Toast notifications', status: 'complete' as const },
        { name: 'Confirmation dialogs', status: 'complete' as const },
      ]
    },
    {
      name: 'Phase 2: Development',
      color: 'amber',
      items: [
        { name: 'Real API integration', status: 'planned' as const },
        { name: 'Database schema', status: 'planned' as const },
        { name: 'Authentication system', status: 'planned' as const },
        { name: 'Offline-first architecture', status: 'planned' as const },
        { name: 'Data sync engine', status: 'planned' as const },
      ]
    },
    {
      name: 'Phase 3: Field Pilot',
      color: 'red',
      items: [
        { name: 'NRC Sudan deployment', status: 'planned' as const },
        { name: 'Multi-language support', status: 'planned' as const },
        { name: 'Field training modules', status: 'planned' as const },
        { name: 'Performance optimization', status: 'planned' as const },
      ]
    },
  ];

  const completedCount = categories.flatMap(c => c.items).filter(i => i.status === 'complete').length;
  const totalCount = categories.flatMap(c => c.items).length;
  const progressPercent = Math.round((completedCount / totalCount) * 100);

  return (
    <SlideLayout>
      <div className="flex-1 p-12">
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="text-accent-500 text-sm font-semibold tracking-widest uppercase mb-2">
              Project Status
            </div>
            <h2 className="text-4xl font-bold text-gray-900">
              What's Built vs. Planned
            </h2>
          </div>

          {/* Overall Progress */}
          <div className="text-right">
            <div className="text-5xl font-bold text-accent-500">{progressPercent}%</div>
            <div className="text-sm text-gray-500">Phase 1 Complete</div>
            <div className="text-xs text-gray-400 mt-1">{completedCount} of {totalCount} items</div>
          </div>
        </div>

        {/* Progress Categories */}
        <div className="grid grid-cols-5 gap-4">
          {categories.map((category) => (
            <ProgressCategory key={category.name} category={category} />
          ))}
        </div>

        {/* Legend */}
        <div className="mt-8 flex items-center justify-center gap-8 pt-6 border-t border-gray-200">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500" />
            <span className="text-sm text-gray-600">Complete</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-amber-500" />
            <span className="text-sm text-gray-600">In Progress</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-300" />
            <span className="text-sm text-gray-600">Planned</span>
          </div>
        </div>
      </div>
    </SlideLayout>
  );
}

function ProgressCategory({ category }: {
  category: {
    name: string;
    color: string;
    items: { name: string; status: 'complete' | 'in-progress' | 'planned' }[]
  }
}) {
  const colorMap: Record<string, string> = {
    purple: 'border-t-purple-500',
    blue: 'border-t-blue-500',
    green: 'border-t-green-500',
    amber: 'border-t-amber-500',
    red: 'border-t-red-500',
  };

  const headerColorMap: Record<string, string> = {
    purple: 'bg-purple-50',
    blue: 'bg-blue-50',
    green: 'bg-green-50',
    amber: 'bg-amber-50',
    red: 'bg-red-50',
  };

  const completedInCategory = category.items.filter(i => i.status === 'complete').length;
  const totalInCategory = category.items.length;

  return (
    <div className={cn('border border-gray-200 border-t-4', colorMap[category.color])}>
      <div className={cn('px-4 py-3', headerColorMap[category.color])}>
        <div className="text-xs font-semibold text-gray-700 truncate">{category.name}</div>
        <div className="text-[10px] text-gray-500 mt-0.5">
          {completedInCategory}/{totalInCategory} complete
        </div>
        {/* Mini progress bar */}
        <div className="h-1 bg-gray-200 mt-2">
          <div
            className="h-full bg-green-500 transition-all"
            style={{ width: `${(completedInCategory / totalInCategory) * 100}%` }}
          />
        </div>
      </div>
      <div className="p-3 space-y-2">
        {category.items.map((item, i) => (
          <ProgressItem key={i} name={item.name} status={item.status} />
        ))}
      </div>
    </div>
  );
}

function ProgressItem({ name, status }: { name: string; status: 'complete' | 'in-progress' | 'planned' }) {
  const statusConfig = {
    'complete': { icon: <CheckCircle2 className="w-3 h-3 text-green-600" />, textClass: 'text-gray-700' },
    'in-progress': { icon: <Clock className="w-3 h-3 text-amber-500" />, textClass: 'text-gray-700' },
    'planned': { icon: <Circle className="w-3 h-3 text-gray-300" />, textClass: 'text-gray-400' },
  };

  const config = statusConfig[status];

  return (
    <div className="flex items-start gap-2">
      <div className="mt-0.5 flex-shrink-0">{config.icon}</div>
      <span className={cn('text-[11px] leading-tight', config.textClass)}>{name}</span>
    </div>
  );
}

function CallToActionSlide() {
  return (
    <SlideLayout variant="dark">
      <div className="flex-1 flex flex-col items-center justify-center px-16 text-center">
        <div className="text-accent-500 text-sm font-semibold tracking-widest uppercase mb-6">
          Decision Point
        </div>
        <h1 className="text-5xl font-bold text-white mb-6">
          What We're Asking For
        </h1>
        <p className="text-xl text-gray-400 max-w-2xl mb-12">
          Your approval to proceed with the next phase of CLEAR development
        </p>

        <div className="grid grid-cols-3 gap-8 max-w-4xl">
          <div className="bg-gray-800 border border-gray-700 p-6 text-left">
            <div className="w-10 h-10 bg-accent-500 flex items-center justify-center mb-4">
              <span className="text-white font-bold">1</span>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Approve Pilot</h3>
            <p className="text-sm text-gray-400">
              Authorize field pilot with NRC Sudan teams to validate prototypes in real operations
            </p>
          </div>

          <div className="bg-gray-800 border border-gray-700 p-6 text-left">
            <div className="w-10 h-10 bg-accent-500 flex items-center justify-center mb-4">
              <span className="text-white font-bold">2</span>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Resource Allocation</h3>
            <p className="text-sm text-gray-400">
              Allocate development resources for Phase 2 core module build-out
            </p>
          </div>

          <div className="bg-gray-800 border border-gray-700 p-6 text-left">
            <div className="w-10 h-10 bg-accent-500 flex items-center justify-center mb-4">
              <span className="text-white font-bold">3</span>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Partner Engagement</h3>
            <p className="text-sm text-gray-400">
              Initiate conversations with partner organizations for multi-org ownership
            </p>
          </div>
        </div>

        <div className="mt-12 flex items-center gap-6">
          <div className="flex items-center gap-2 text-gray-500">
            <Calendar className="w-4 h-4" />
            <span className="text-sm">Decision needed by end of quarter</span>
          </div>
        </div>
      </div>
    </SlideLayout>
  );
}

// ============================================================================
// RECOMMENDATIONS & CLOSING SLIDES
// ============================================================================

function RecommendationsSlide() {
  return (
    <SlideLayout>
      <div className="flex-1 p-16">
        <div className="text-accent-500 text-sm font-semibold tracking-widest uppercase mb-4">
          Looking Forward
        </div>
        <h2 className="text-4xl font-bold text-gray-900 mb-12">
          Next Steps & Recommendations
        </h2>

        <div className="grid grid-cols-2 gap-12">
          {/* Priority 3 */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-600" />
              Priority 3: Polish & Refinement
            </h3>
            <div className="space-y-4">
              <RecommendationItem
                title="Micro-animations"
                description="Subtle transitions for hover, click, and state changes"
                effort="Low"
                impact="Low"
              />
              <RecommendationItem
                title="Mobile Responsiveness"
                description="Optimize layouts for tablet and mobile devices"
                effort="High"
                impact="Medium"
              />
              <RecommendationItem
                title="Accessibility"
                description="ARIA labels, focus management, screen reader support"
                effort="Medium"
                impact="Medium"
              />
              <RecommendationItem
                title="Keyboard Navigation"
                description="Full keyboard support for all interactions"
                effort="Medium"
                impact="Medium"
              />
            </div>
          </div>

          {/* Strategic */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-600" />
              Strategic Recommendations
            </h3>
            <div className="space-y-4">
              <RecommendationItem
                title="Real API Integration"
                description="Connect to actual backend services"
                effort="High"
                impact="High"
              />
              <RecommendationItem
                title="Offline Support"
                description="Service worker for field connectivity issues"
                effort="High"
                impact="High"
              />
              <RecommendationItem
                title="Multi-language Support"
                description="i18n framework for regional deployments"
                effort="Medium"
                impact="High"
              />
              <RecommendationItem
                title="Performance Optimization"
                description="Code splitting, lazy loading, caching"
                effort="Medium"
                impact="Medium"
              />
            </div>
          </div>
        </div>
      </div>
    </SlideLayout>
  );
}

function RecommendationItem({
  title,
  description,
  effort,
  impact
}: {
  title: string;
  description: string;
  effort: 'Low' | 'Medium' | 'High';
  impact: 'Low' | 'Medium' | 'High';
}) {
  const effortColors = {
    Low: 'bg-green-100 text-green-700',
    Medium: 'bg-amber-100 text-amber-700',
    High: 'bg-red-100 text-red-700',
  };

  const impactColors = {
    Low: 'bg-gray-100 text-gray-700',
    Medium: 'bg-blue-100 text-blue-700',
    High: 'bg-accent-100 text-accent-700',
  };

  return (
    <div className="bg-white border border-gray-200 p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-medium text-gray-900">{title}</div>
          <div className="text-sm text-gray-500 mt-0.5">{description}</div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <span className={cn('text-[10px] px-2 py-0.5', effortColors[effort])}>
            {effort} Effort
          </span>
          <span className={cn('text-[10px] px-2 py-0.5', impactColors[impact])}>
            {impact} Impact
          </span>
        </div>
      </div>
    </div>
  );
}

function ClosingSlide() {
  return (
    <SlideLayout variant="accent">
      <div className="flex-1 flex flex-col items-center justify-center px-16 text-center">
        <h1 className="text-5xl font-bold text-white mb-6">
          Research-Driven Design
        </h1>
        <p className="text-xl text-blue-100 max-w-2xl mb-12">
          Every design decision is grounded in field practitioner research.
          The CLEAR prototypes are ready for user testing with humanitarian workers.
        </p>

        <div className="grid grid-cols-5 gap-6 text-center">
          <div>
            <div className="text-4xl font-bold text-white mb-2">4</div>
            <div className="text-sm text-blue-200">User Personas</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-white mb-2">10+</div>
            <div className="text-sm text-blue-200">Pain Points Solved</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-white mb-2">4</div>
            <div className="text-sm text-blue-200">Integrated Modules</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-white mb-2">30+</div>
            <div className="text-sm text-blue-200">UI Components</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-white mb-2">6</div>
            <div className="text-sm text-blue-200">Test Scenarios</div>
          </div>
        </div>
      </div>

      <div className="px-16 py-8 border-t border-blue-400/30 flex justify-between text-sm text-blue-200">
        <span>CLEAR Humanitarian System — NRC Sudan Research</span>
        <span>docs/WIREFRAME-FEATURES.md for full documentation</span>
      </div>
    </SlideLayout>
  );
}

export default WireframePresentation;
